import { Box, Divider } from "@mui/material";
import Grid from "@mui/material/Grid2";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Title from "../../components/common/Title";
import { ValidationAlert } from "../../components/common/ValidationAlert";
import Layout from "../../components/layout/Layout";
import TopBarGridContainer from "../../components/layout/TopBar";
import useAlert from "../../hooks/useAlert";
import { useNatGateway } from "../../hooks/useNatGateway";
import { initAuto4clientTask } from "../../services/aws/ecs";
import ActionButtons from "./components/ActionButtons";
import TaskInputs from "./components/DataPreparation";
import useCompanyNames from "./hooks/useCompanyNames";

function Auto4Client() {
  const navigate = useNavigate();
  const { alertState, showAlert, closeAlert } = useAlert();
  const {
    isReady: natGatewayReady,
    isLoading: natGatewayLoading,
    checkStatus,
    createGateway,
  } = useNatGateway("scraper-natgw");
  const { companyNames } = useCompanyNames();
  const [taskInputs, setTaskInputs] = useState({
    selectedCompany: null,
    startDate: dayjs().subtract(1, "day"),
    endDate: dayjs(),
    overwriteExistingDBRows: true,
    includePreviouslyClassified: true,
    competitiveAnalysis: false,
    dataName: "",
    platforms: [],
  });

  const validateInputs = () => {
    if (!taskInputs.selectedCompany) {
      showAlert("error", "Please select a Company!");
      return false;
    }
    if (taskInputs.dataName && taskInputs.dataName.length === 0) {
      showAlert("error", "Please enter a Data Name!");
      return false;
    }
    if (taskInputs.startDate >= taskInputs.endDate) {
      showAlert("error", "Start Date must be before End Date!");
      return false;
    }
    if (taskInputs.platforms.length === 0) {
      showAlert("error", "Please select at least one Platform!");
      return false;
    }
    return true;
  };

  // Handlers
  const handleSubmitTask = async () => {
    if (!validateInputs()) {
      return;
    }

    const res = await initAuto4clientTask(
      taskInputs.selectedCompany,
      taskInputs.startDate,
      taskInputs.endDate,
      taskInputs.overwriteExistingDBRows,
      taskInputs.includePreviouslyClassified,
      taskInputs.competitiveAnalysis,
      taskInputs.dataName,
      taskInputs.platforms
    );
    if (!res.ok) {
      // A failure here is often a bare HTML 500, so don't assume the body parses.
      const body = await res.json().catch(() => ({}));
      showAlert("error", body.error || "Failed to start task.");
      return;
    }

    showAlert("success", "Success!");
    setTimeout(() => {
      navigate("/tasks");
    }, 3000);
  };

  const handleCreateNatGateway = async () => {
    showAlert("info", "Creating NAT Gateway, please wait...");
    createGateway();
  };

  const handleTaskInputsChange = (newInputs) => {
    setTaskInputs(newInputs);
  };

  // Check NAT Gateway status on component mount
  useEffect(() => {
    checkStatus();
  }, []);

  return (
    <Layout>
      <ValidationAlert
        inputOpen={alertState.alertOpen}
        setInputClosed={closeAlert}
        severity={alertState.alertType}
        alertMessage={alertState.alertMsg}
      />

      {/* Top bar */}
      <TopBarGridContainer>
        <Grid>
          <Title text={"AUTO4CLIENT"} sx={{ my: { xs: 2, sm: 5 } }} />
        </Grid>
        <Grid size={{ xs: 12, sm: "grow" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: { xs: "start", sm: "end" },
              mt: { xs: 0, sm: 5 },
            }}
          >
            {/* Action Buttons */}
            <ActionButtons
              onSubmit={handleSubmitTask}
              onCreateGateway={handleCreateNatGateway}
              natGatewayReady={natGatewayReady}
              natGatewayLoading={natGatewayLoading}
            />
          </Box>
        </Grid>
      </TopBarGridContainer>
      <Divider variant="middle" />

      {/* Using TabPanel components instead of conditional rendering */}
      <TaskInputs companyNames={companyNames} taskInputs={taskInputs} onInputsChange={handleTaskInputsChange} />
    </Layout>
  );
}

export default Auto4Client;
